import { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';

import { RiArrowRightLine, RiExternalLinkLine } from '@remixicon/react';

import { Card, CardBody, Flex, Text } from '@backstage/ui';

import {
  extractLastNameComponent,
  pathToModuleStyle,
} from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  ArtifactIcon,
  DEFAULT_ARTIFACT_ICON,
  isExternalLink,
  useArtifactExtensions,
} from '@spotify/plugin-confidence-artifacts-react';

import { getArtifactNavigationPath } from '../../artifactUtils';

const useStyles = makeStyles({
  cardButton: {
    background: 'none',
    border: 0,
    cursor: 'pointer',
    display: 'block',
    height: '100%',
    padding: 0,
    textAlign: 'inherit',
    width: '100%',
    '&:hover $card': {
      backgroundColor: 'var(--bui-bg-neutral-3)',
    },
    '&:focus-visible $card': {
      outline: '2px solid var(--bui-ring)',
      outlineOffset: '2px',
    },
  },
  card: {
    backgroundColor: 'var(--bui-bg-neutral-2)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    transition: 'background-color 0.15s ease',
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  actionIcon: {
    alignItems: 'center',
    borderRadius: 'var(--bui-radius-1)',
    color: 'var(--bui-fg-secondary)',
    display: 'inline-flex',
    flexShrink: 0,
    height: 24,
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.15s ease',
    width: 24,
    '$cardButton:hover &': {
      opacity: 1,
    },
  },
});

interface DefaultArtifactCardProps {
  artifact: AttachmentFragment;
  resolvedDisplayName?: string;
  action?: ReactNode;
  onClick: () => void;
}

const stopPropagation = (e: MouseEvent) => e.stopPropagation();

export const DefaultArtifactCard = ({
  artifact,
  resolvedDisplayName,
  action,
  onClick,
}: DefaultArtifactCardProps) => {
  const classes = useStyles();
  const extensions = useArtifactExtensions();
  const ext = extensions.find(e => e.predicate(artifact));

  const { Icon } = ext?.icon?.(artifact) ?? DEFAULT_ARTIFACT_ICON;
  const typeLabel =
    pathToModuleStyle(artifact.uri)?.label ?? ext?.label ?? 'Link';
  const fallbackName = extractLastNameComponent(artifact.uri);

  const navigationPath = getArtifactNavigationPath(artifact.uri);
  const isExternal = isExternalLink(artifact.uri);

  return (
    <div
      role="button"
      tabIndex={0}
      className={classes.cardButton}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
    >
      <Card className={classes.card}>
        <CardBody>
          <Flex align="center" gap="2">
            <ArtifactIcon>
              <Icon size={16} />
            </ArtifactIcon>
            <Flex className={classes.info} direction="column" gap="0">
              <Text variant="body-medium" weight="bold" truncate>
                {artifact.displayName || resolvedDisplayName || fallbackName}
              </Text>
              <Text variant="body-small" color="secondary" truncate>
                {typeLabel}
              </Text>
            </Flex>
            {action && (
              <span className={classes.actionIcon} aria-hidden>
                {action}
              </span>
            )}
            {!action && isExternal && (
              <a
                href={artifact.uri}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.actionIcon}
                onClick={stopPropagation}
                aria-label={`Go to ${typeLabel}`}
              >
                <RiExternalLinkLine size={16} />
              </a>
            )}
            {!action && navigationPath && (
              <Link
                to={`/${navigationPath}`}
                className={classes.actionIcon}
                onClick={stopPropagation}
                aria-label={`Go to ${typeLabel}`}
              >
                <RiArrowRightLine size={16} />
              </Link>
            )}
          </Flex>
        </CardBody>
      </Card>
    </div>
  );
};
